import { useRouter } from 'next/router'
import { ReactNode, useState } from 'react'

import {
  EmailIcon,
  FacebookIcon,
  PinterestIcon,
  ShareIcon,
  WeiboIcon,
  XLogoIcon,
} from '@nx/fire/assets'
import { FireButton } from '@nx/fire/button'
import { FireTooltip } from '@nx/fire/tooltip'
import { Heading } from '@nx/fire/typography'
import { getTranslate } from '@nx/translations'

import {
  ShareSheetWrapper,
  SocialNetwork,
  SocialNetworkIcon,
} from './fire-share-sheet.styles'
import translate from './fire-share-sheet.translate.json'

const { text } = getTranslate(translate)

export const ShareSheet = ({ title, type, url }: ShareSheetProps) => {
  const router = useRouter()
  const [popupOpen, setPopupOpen] = useState(false)

  const onShareButtonClicked = async () => {
    const shareData = {
      url: `${url}${router.asPath}`,
      title: title,
    }

    /* istanbul ignore next */
    if (navigator.canShare && navigator.canShare(shareData)) {
      navigator.share(shareData).catch((e: DOMException) => {
        if (e.name !== 'AbortError') {
          setPopupOpen(!popupOpen)
        }
      })
    } else {
      setPopupOpen(!popupOpen)
    }
  }

  return (
    <FireTooltip
      showTooltip={popupOpen}
      onCloseClick={() => setPopupOpen(false)}
      tooltip={
        <TooltipComponent
          type={type}
          title={title}
          url={`${url}${router.asPath}`}
        />
      }
    >
      <FireButton
        variant="ghost"
        startIcon={ShareIcon}
        onClick={onShareButtonClicked}
      >
        {text('buttonText')}
      </FireButton>
    </FireTooltip>
  )
}

const TooltipComponent = ({ type, title, url }: ShareSheetProps) => {
  const SOCIAL_NETWORKS: SocialNetwork[] = [
    {
      name: text('shareOption.facebook'),
      icon: <FacebookIcon />,
      path: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    },
    {
      name: text('shareOption.x'),
      icon: <XLogoIcon />,
      path: ({ title, url }) =>
        `https://x.com/intent/tweet?text=${title}&url=${url}&via=bonhams1793`,
    },
    {
      name: text('shareOption.pinterest'),
      icon: <PinterestIcon />,
      path: ({ url }) => `https://pinterest.com/pin/create/button/?url=${url}`,
    },
    {
      name: text('shareOption.weibo'),
      icon: <WeiboIcon />,
      path: ({ url }) => `http://service.weibo.com/share/share.php?url=${url}`,
    },
    {
      name: text('shareOption.email'),
      icon: <EmailIcon />,
      path: ({ title, url }) => `mailto:?subject=${title}&body=${url}`,
    },
  ]

  return (
    <>
      <Heading $level={5}>{text('title', { type })}</Heading>
      <ShareSheetWrapper>
        {SOCIAL_NETWORKS.map((network: SocialNetwork, index: number) => (
          <SocialNetwork
            key={`network-${index}`}
            href={network.path({ title, url })}
            target="_blank"
            rel="noreferrer"
          >
            <SocialNetworkIcon>{network.icon}</SocialNetworkIcon>

            {network.name}
          </SocialNetwork>
        ))}
      </ShareSheetWrapper>
    </>
  )
}

interface SocialNetwork {
  name: string
  icon: ReactNode
  path: ({ title, url }: { title?: string; url: string }) => string
}

interface ShareSheetProps {
  title: string
  type: string
  url: string
}
